// Paywall experiments (contracts/paywall.md §3, §5; backend/api src/schemas/paywall.ts, routes/admin/paywall.ts):
// A/B tests of the paywall variant, with sticky assignment and attributed results. Every role reads; writes (and
// the paywall.* config) are admin. Only one experiment runs at a time. Kept apart from client.ts / queries.ts like
// the other feature modules.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { EXPERIMENT_STATUSES, VARIANT_IDS, Variant, type VariantId } from "../lib/paywall";
import { request } from "./client";

const count = z.number().int().min(0);
const rate = z.number().min(0).max(1);

/** Variants come back as stored; parsed loosely so an older stored shape still shows. */
export const ExperimentVariant = z.object({ id: z.enum(VARIANT_IDS), weight: z.number().int(), config: Variant.catch({}) });
export type ExperimentVariant = z.infer<typeof ExperimentVariant>;

export const Experiment = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(EXPERIMENT_STATUSES),
  /** new: accounts created after the first start; all_free: every free player. */
  audience: z.enum(["new", "all_free"]),
  variants: z.array(ExperimentVariant),
  winner: z.enum(VARIANT_IDS).nullable(),
  startedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Players assigned per variant id. */
  assigned: z.record(z.string(), count),
});
export type Experiment = z.infer<typeof Experiment>;
export const Experiments = z.object({ items: z.array(Experiment) });

export interface ExperimentDraft {
  name: string;
  audience: "new" | "all_free";
  variants: { id: VariantId; weight: number; config: Variant }[];
}
/** PATCH: audience and variants in draft or paused (paused keeps the variant ids); status running | paused | ended; the winner once ended. */
export type ExperimentPatch = Partial<ExperimentDraft> & { status?: "running" | "paused" | "ended"; winner?: VariantId | null };

const Interval = z.object({ rate: rate.nullable(), low: rate.nullable(), high: rate.nullable() });

/** GET /paywall/experiments/{id}/results?from&to (UTC days; default from its start to today or its end). */
export const ExperimentResults = z.object({
  experiment: Experiment,
  from: z.string(),
  to: z.string(),
  /** Monte Carlo draws behind probabilityBest (seeded). */
  draws: z.number().int(),
  /** Purchased products without a USD price in paywall.prices (counted as 0). */
  unpriced: z.array(z.string()),
  variants: z.array(
    z.object({
      id: z.enum(VARIANT_IDS),
      weight: z.number().int(),
      assigned: count,
      /** Paywalls shown in this variant: unique players and in total. */
      impressions: z.object({ players: count, total: count }),
      purchasers: count,
      trialStarts: count,
      /** Purchases paid at once (no free trial), lifetime included. */
      paidStarts: count,
      purchasesByProduct: z.record(z.string(), count),
      /** Purchasers who were shown this variant ÷ players shown, with a Wilson 95% interval. */
      conversion: Interval.extend({ converted: count, shown: count }),
      /** known = converted or ended; rate null until some are known. */
      trialToPaid: z.object({ trials: count, known: count, converted: count, rate: rate.nullable() }),
      refunds: count,
      estRevenueUsd: z.number().min(0),
      probabilityBest: rate,
    }),
  ),
});
export type ExperimentResults = z.infer<typeof ExperimentResults>;
export type VariantResult = ExperimentResults["variants"][number];

const at = (id: string) => `/paywall/experiments/${encodeURIComponent(id)}`;

export const experimentsApi = {
  list: (signal?: AbortSignal) => request(Experiments, "GET", "/paywall/experiments", { signal }),
  get: (id: string, signal?: AbortSignal) => request(Experiment, "GET", at(id), { signal }),
  create: (d: ExperimentDraft) => request(Experiment, "POST", "/paywall/experiments", { body: d }),
  update: (id: string, patch: ExperimentPatch) => request(Experiment, "PATCH", at(id), { body: patch }),
  results: (id: string, range: { from?: string; to?: string } = {}, signal?: AbortSignal) => request(ExperimentResults, "GET", `${at(id)}/results`, { query: range, signal }),
};

export const experimentKeys = { all: ["experiments"] as const, list: ["experiments", "list"] as const, results: (id: string) => ["experiments", "results", id] as const };

export const useExperiments = () => useQuery({ queryKey: experimentKeys.list, queryFn: ({ signal }) => experimentsApi.list(signal) });
export const useExperimentResults = (id: string | null) =>
  useQuery({ queryKey: experimentKeys.results(id ?? ""), queryFn: ({ signal }) => experimentsApi.results(id!, {}, signal), enabled: Boolean(id), placeholderData: keepPreviousData });

function useExperimentMutation<V, R>(fn: (v: V) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: experimentKeys.all });
      void client.invalidateQueries({ queryKey: ["audit"] });
    },
  });
}

export const useCreateExperiment = () => useExperimentMutation((d: ExperimentDraft) => experimentsApi.create(d));
export const useUpdateExperiment = () => useExperimentMutation(({ id, patch }: { id: string; patch: ExperimentPatch }) => experimentsApi.update(id, patch));
